import { Stack, type StackProps } from '@mantine/core'
import { ErrorBoundary } from '@suspensive/react'
import { QueryErrorResetBoundary } from '@tanstack/react-query'
import type { ComponentType, PropsWithChildren } from 'react'

import { OGSectionErrorBoundaryFallback, SectionErrorBoundaryFallback } from './fallback'

interface SectionErrorBoundaryOptions {
  containerProps?: StackProps
  variant?: 'default' | 'og'
  title?: string
}

export function SectionErrorBoundary({
  children,
  containerProps,
  variant = 'default',
  title,
}: PropsWithChildren<SectionErrorBoundaryOptions>) {
  const Fallback = variant === 'og' ? OGSectionErrorBoundaryFallback : SectionErrorBoundaryFallback
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <Stack justify="center" {...containerProps}>
          <ErrorBoundary onReset={reset} fallback={props => <Fallback {...props} title={title} />}>
            {children}
          </ErrorBoundary>
        </Stack>
      )}
    </QueryErrorResetBoundary>
  )
}

// SectionErrorBoundary HOC
function withSectionErrorBoundary<P extends object>(
  Component: ComponentType<P>,
  options?: SectionErrorBoundaryOptions,
) {
  return function WithSectionErrorBoundary(props: P) {
    return (
      <SectionErrorBoundary {...options}>
        <Component {...props} />
      </SectionErrorBoundary>
    )
  }
}

SectionErrorBoundary.with = withSectionErrorBoundary
